import React, { useEffect, useMemo, useState, ReactNode } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Globe2,
  Leaf,
  Building2,
  Landmark,
  MapPin,
  Network,
  ArrowRight,
  Filter,
  Search,
  Sparkles,
  Calendar,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

//
// COMMUNITIES PAGE (single file)
// — Same stack/style as your EDA site
// — Pure React + TailwindCSS + framer-motion + lucide-react
// — Can be used as a standalone page or dropped into your existing app
//
// HOW TO USE
// 1) Keep file as src/CommunitiesPage.jsx
// 2) Add a nav link to "#/communities" or render <CommunitiesPage/> on a route
//    If you don’t use react-router, see the HashRouter-lite hook at the bottom.
// 3) The join form is client-side only; wire it to your backend/Formspree/Netlify later.
//

// ================= SafeLink =================
function extractHashId(href) {
  if (!href || href[0] !== '#') return null;
  const after = href.slice(1);
  const id = after.split(/[?&]/)[0].trim();
  return id || null;
}
const SafeLink = ({ href, className = "", children, onClick=(e: any)=>{} }) => {
  const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  const handleClick = (e) => {
    e.preventDefault();
    onClick && onClick(e);
    if (href.startsWith('#')) {
      const id = extractHashId(href);
      if (id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.location.hash = href;
    }
  };
  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};



// ============== SHARED UI ==============
const Section: React.FC<{ id?: string; className?: string; children: ReactNode }> = ({ id, className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const Card: React.FC<{ className?: string; children: ReactNode }> = ({ className = "", children }) => (
  <div className={`rounded-2xl shadow-lg bg-white/90 backdrop-blur border border-slate-100 ${className}`}>{children}</div>
);

const H2: React.FC<{ children: ReactNode; kicker?: string; center?: boolean }> = ({ children, kicker, center }) => (
  <div className={`mb-8 ${center ? "text-center" : ""}`}>
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-indigo-600">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);

const Pill: React.FC<{ children: ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-slate-900 text-white px-3 py-1 rounded-full">
    {children}
  </span>
);

const Badge: React.FC<{ children: ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
    {children}
  </span>
);

// ============== DATA ==============
const verticals = [
  { key: "dpi", label: "Digital Public Infrastructure", icon: Network, color: "from-indigo-600 to-violet-600" },
  { key: "women", label: "Women Entrepreneurs", icon: Users, color: "from-rose-500 to-fuchsia-600" },
  { key: "esg", label: "ESG & Climate", icon: Leaf, color: "from-emerald-500 to-teal-600" },
  { key: "industry", label: "Industry & Sector", icon: Building2, color: "from-sky-500 to-cyan-600" },
  { key: "regional", label: "Regional & Cluster", icon: MapPin, color: "from-amber-500 to-orange-600" },
];

const dpiCommunities = [
  { id: "ondc", name: "ONDC Adoption Circle", blurb: "Seller onboarding, logistics, and network growth.", members: 1320 },
  { id: "ulip", name: "ULIP Logistics Hub", blurb: "Unified logistics interface for MSMEs & fleets.", members: 980 },
  { id: "ocen", name: "OCEN Credit Access Guild", blurb: "Open credit rails for SMEs and partners.", members: 740 },
  { id: "dsep", name: "DSEP Skills Network", blurb: "Distributed skilling & learning pathways.", members: 1120 },
];

const womenCommunities = [
  { id: "we-maker", name: "Women Maker Guild", blurb: "Products to platforms — brand, sell, scale.", members: 1680 },
  { id: "we-agri", name: "Agri Women Collective", blurb: "Farmgate to D2C with ONDC & Digi logistics.", members: 820 },
  { id: "we-services", name: "Services & Creators", blurb: "Agencies, coaches, creators, and pros.", members: 560 },
];

const esgCommunities = [
  { id: "climate", name: "Climate & Circularity", blurb: "Reverse logistics, green ops, carbon math.", members: 610 },
  { id: "csr", name: "CSR Impact League", blurb: "Programs, pilots, and reporting frameworks.", members: 430 },
];

const industryCommunities = [
  { id: "fnb", name: "Food & Beverage", blurb: "Q‑commerce, HORECA supply, cold chain.", members: 1050 },
  { id: "fmcg", name: "FMCG & Personal Care", blurb: "Distributor → Agent models, pricebooks.", members: 990 },
  { id: "fashion", name: "Fashion & Apparel", blurb: "Clusters, artisans, and brand ops.", members: 720 },
  { id: "health", name: "Health & Wellness", blurb: "Clinics, diagnostics, and retail.", members: 640 },
];

const regionalCommunities = [
  { id: "cbe", name: "Coimbatore Cluster", blurb: "Manufacturing, textiles, and EV logistics.", members: 1450 },
  { id: "tup", name: "Tiruppur Knitwear", blurb: "Export readiness & digital catalogs.", members: 880 },
  { id: "chn", name: "Chennai Metro", blurb: "Mobility, ports, and startup hubs.", members: 1200 },
  { id: "try", name: "Trichy & Delta", blurb: "Agri value chains & MSME growth.", members: 760 },
];

const events = [
  { date: "2025-09-05", title: "ONDC Seller Bootcamp (Coimbatore)", tag: "DPI", icon: Network },
  { date: "2025-09-12", title: "Women Entrepreneurs Demo Day (Chennai)", tag: "Women", icon: Users },
  { date: "2025-09-20", title: "ESG Reverse Logistics Roundtable (Bengaluru)", tag: "ESG", icon: Leaf },
  { date: "2025-10-01", title: "FMCG Distributor → Agent Playbook (Webinar)", tag: "Industry", icon: Building2 },
];

// ============== HELPERS ==============
const humanNumber = (n: number) => new Intl.NumberFormat("en-IN").format(n);

// ============== PAGE SECTIONS ==============
const Nav = () => {

  const submenus = [
    // // { href: "#home", label: "Home"},
    { href: "#directory", label: "Directory"},
    { href: "#faq", label: "FAQ"},
    { href: "#contact", label: "Contact"},
  ];

  const [openMobile, setOpenMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
          <Section id="one-digiit" className="flex flex-row h-12 w-full items-center justify-right">
            {/* Second row: submenus (desktop only) */}
            <div className="hidden md:block flex flex-row w-full items-center justify-right">
              <Section
                id="nav-sub"
                className="flex items-center gap-4 py-2 overflow-x-auto justify-end"
              >
                {submenus.map((i) => (
                  <SafeLink
                    key={i.href}
                    href={i.href}
                    className="text-sm text-slate-600 hover:text-slate-900 whitespace-nowrap"
                  >
                    {i.label}
                  </SafeLink>
                ))}
    
                {/* CTA */}
                <SafeLink
                  href="#contact"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600 shrink-0"
                >
                  Talk to Us <ArrowRight className="w-4 h-4" />
                </SafeLink>
              </Section>
            </div>
    
    
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setOpenMobile((v) => !v)}
              aria-label="Toggle menu"
            >
              <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
              <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
              <span className="block w-6 h-0.5 bg-slate-900"></span>
            </button>
          </Section>
    
          {/* Mobile dropdown */}
          {openMobile && (
            <div className="md:hidden border-t border-slate-200 bg-white">
              <Section id={''} className="py-4 flex flex-col gap-3">
                <div className="mt-4 border-t border-slate-200 pt-4">
                  {submenus.map((i) => (
                    <SafeLink
                      key={i.href}
                      href={i.href}
                      onClick={() => setOpenMobile(false)}
                      className="text-slate-600"
                    >
                      {i.label}
                    </SafeLink>
                  ))}
                </div>
                <SafeLink
                  href="#contact"
                  onClick={() => setOpenMobile(false)}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                >
                  Talk to Us <ArrowRight className="w-4 h-4" />
                </SafeLink>
              </Section>
            </div>
          )}
        </header>
  );
};

const Hero: React.FC = () => (
  <div className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.06),transparent_35%)]"/>
    <Section className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}}>
          <div className="flex gap-2 mb-4">
            <Badge>Free to Join</Badge>
            <Badge>Open Ecosystem</Badge>
            <Badge>City Chapters</Badge>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">Community Network for DPI Adoption</h1>
          <p className="mt-4 text-lg md:text-xl text-slate-200 max-w-2xl">From startups to MSMEs to enterprises — join communities that help you adopt Digital Public Infrastructures (ONDC, ULIP, OCEN, DSEP) and grow with peers, mentors, and partners.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#join" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 font-semibold">Join a Community <ArrowRight className="w-4 h-4"/></a>
            <a href="#chapters" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3">Explore Chapters</a>
          </div>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4"/> Meetups & webinars every month</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4"/> Playbooks & partner access</span>
          </div>
        </motion.div>
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6, delay:0.1}}>
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Pill><Globe2 className="w-4 h-4"/> Community Pillars</Pill>
              <span className="text-slate-500">What you’ll find here</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {verticals.map(v => (
                <li key={v.key} className="rounded-xl p-4 border border-slate-200 hover:shadow-md transition">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${v.color} grid place-items-center text-white mb-2`}>
                    {React.createElement(v.icon, { className: "w-5 h-5" })}
                  </div>
                  <div className="font-semibold text-slate-900">{v.label}</div>
                  <div className="mt-1 text-sm text-slate-600">Resources, meetups, and peer support</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-slate-500">Zero fees to join. Advanced workshops and certifications available via EDA.</div>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

const Directory: React.FC = () => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<string>("all");

  const groups = useMemo(() => ([
    { title: "DPI Communities", icon: Network, data: dpiCommunities, key: "dpi" },
    { title: "Women Entrepreneurs", icon: Users, data: womenCommunities, key: "women" },
    { title: "ESG & Climate", icon: Leaf, data: esgCommunities, key: "esg" },
    { title: "Industry & Sector", icon: Building2, data: industryCommunities, key: "industry" },
    { title: "Regional & Cluster", icon: MapPin, data: regionalCommunities, key: "regional" },
  ]), []);

  const visible = (list: any[]) => list.filter(x => (
    (filter === "all" || groups.find(g => g.data.includes(x))?.key === filter) &&
    (q.trim().length === 0 || x.name.toLowerCase().includes(q.toLowerCase()) || x.id.toLowerCase().includes(q.toLowerCase()))
  ));

  return (
    <Section id="directory" className="py-16">
      <H2 kicker="Communities" center>Browse & join a chapter</H2>
      <Card className="p-4 md:p-6 mb-6">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2">
            <Search className="w-4 h-4 text-slate-500"/>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search community name…" className="w-full outline-none"/>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-2">
            <Filter className="w-4 h-4 text-slate-500"/>
            <select value={filter} onChange={e=>setFilter(e.target.value)} className="w-full outline-none">
              <option value="all">All pillars</option>
              {verticals.map(v=> <option key={v.key} value={v.key}>{v.label}</option> )}
            </select>
          </div>
          <a href="#join" className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white px-4 py-2 font-semibold hover:bg-indigo-700"><Sparkles className="w-4 h-4"/> Become a Chapter Lead</a>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {groups.map(g => (
          <motion.div key={g.key} initial={{opacity:0, y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4}}>
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                {React.createElement(g.icon, { className: "w-5 h-5 text-indigo-600" })}
                <div className="font-semibold text-slate-900">{g.title}</div>
              </div>
              <ul className="grid sm:grid-cols-2 gap-4">
                {visible(g.data).slice(0, 8).map((c: any) => (
                  <li key={c.id} className="rounded-xl p-4 border border-slate-200 hover:shadow-md transition">
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <div className="text-sm text-slate-500 line-clamp-2">{c.blurb}</div>
                    <div className="mt-2 text-xs text-slate-500">{humanNumber(c.members)} members</div>
                    <div className="mt-3 flex gap-2">
                      <a href={`#join?community=${c.id}`} className="rounded-full px-3 py-1.5 text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700">Join</a>
                      <a href={`#chapters?community=${c.id}`} className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">View chapters</a>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

const Chapters: React.FC = () => (
  <Section id="chapters" className="py-16">
    <H2 kicker="Regional" center>City & Cluster Chapters</H2>
    <div className="grid md:grid-cols-4 gap-6">
      {["Coimbatore", "Chennai", "Bengaluru", "Tiruppur", "Trichy", "Madurai", "Erode", "Salem"].map((city) => (
        <Card key={city} className="p-5 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600"/>
          <div className="mt-3 font-semibold text-slate-900">{city}</div>
          <div className="text-sm text-slate-500">Monthly meetups • Partner clinics</div>
          <a href="#join" className="mt-4 inline-flex items-center justify-center rounded-full px-4 py-2 bg-slate-900 text-white text-sm font-semibold">Join City</a>
        </Card>
      ))}
    </div>
  </Section>
);

const CalendarList: React.FC = () => (
  <Section id="events" className="py-16">
    <H2 kicker="Events" center>Upcoming meetups & webinars</H2>
    <div className="grid md:grid-cols-2 gap-4">
      {events.map((e) => (
        <Card key={e.title} className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Calendar className="w-4 h-4"/> {new Date(e.date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</div>
          <div className="mt-2 font-semibold text-slate-900">{e.title}</div>
          <div className="mt-3 flex gap-2">
            <a href="#join" className="rounded-full px-3 py-1.5 text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700">RSVP</a>
            <a href="#" className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Details</a>
          </div>
        </Card>
      ))}
    </div>
  </Section>
);

const Benefits: React.FC = () => (
  <Section id="benefits" className="py-16">
    <H2 kicker="Why Join" center>Value for Startups • MSMEs • Enterprises</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {[
        { title: "DPI Playbooks", desc: "Step‑by‑step blueprints to adopt ONDC, ULIP, OCEN, DSEP.", icon: Landmark },
        { title: "Partner Access", desc: "Banks, logistics, SaaS, and channel programs.", icon: Globe2 },
        { title: "Peer Growth", desc: "Chapters, showcases, and growth cohorts.", icon: Users },
      ].map((b) => (
        <Card key={b.title} className="p-6 text-center">
          {React.createElement(b.icon, { className: "w-10 h-10 text-indigo-600 mx-auto" })}
          <div className="mt-3 font-semibold text-slate-900">{b.title}</div>
          <div className="text-sm text-slate-600">{b.desc}</div>
        </Card>
      ))}
    </div>
  </Section>
);

const FAQ: React.FC = () => {
  const items = [
    { q: "Is joining a community free?", a: "Yes. Membership is free. Paid workshops/certifications are optional via the Academy." },
    { q: "Who can join?", a: "Students, founders, MSMEs, enterprise teams, ecosystem partners, and policy folks." },
    { q: "How often are events?", a: "Most chapters host at least one meetup or webinar per month." },
    { q: "Can I lead a chapter?", a: "Absolutely — apply as a Chapter Lead in the Join form below." },
  ];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <Section id="faq" className="py-16">
      <H2 kicker="FAQ" center>Answers to common questions</H2>
      <div className="max-w-3xl mx-auto space-y-3">
        {items.map((f, i) => (
          <Card key={f.q} className="p-4">
            <button className="w-full flex items-center justify-between text-left" onClick={()=>setOpen(open===i?null:i)}>
              <div className="font-semibold text-slate-900">{f.q}</div>
              {open===i ? <ChevronUp className="w-5 h-5 text-slate-500"/> : <ChevronDown className="w-5 h-5 text-slate-500"/>}
            </button>
            {open===i && <div className="mt-2 text-sm text-slate-600">{f.a}</div>}
          </Card>
        ))}
      </div>
    </Section>
  );
};

const JoinForm: React.FC = () => {
  const [form, setForm] = useState({ name:"", email:"", phone:"", city:"", pillar:"dpi", communityId:"", role:"Member", message:"" });
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name || !form.email) { alert("Please fill name & email"); return; }
    setSubmitted(true);
  };

  return (
    <div id="join" className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200">
      <Section className="py-16">
        <H2 kicker="Join" center>Become a member or a Chapter Lead</H2>
        <div className="grid lg:grid-cols-3 gap-8">
          <Card className="p-6 lg:col-span-2">
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-600">Full name</label>
                <input name="name" value={form.name} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your name"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input name="email" value={form.email} onChange={onChange} type="email" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="you@email.com"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Phone</label>
                <input name="phone" value={form.phone} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="+91‑"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">City</label>
                <input name="city" value={form.city} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Your city"/>
              </div>
              <div>
                <label className="text-sm text-slate-600">Pillar</label>
                <select name="pillar" value={form.pillar} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {verticals.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Community</label>
                <select name="communityId" value={form.communityId} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="">Pick a community…</option>
                  {[...dpiCommunities, ...womenCommunities, ...esgCommunities, ...industryCommunities, ...regionalCommunities].map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Role</label>
                <select name="role" value={form.role} onChange={onChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option>Member</option>
                  <option>Volunteer</option>
                  <option>Chapter Lead</option>
                  <option>Partner</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-600">Message</label>
                <textarea name="message" value={form.message} onChange={onChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Tell us about your goals"/>
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-3 items-center">
                <button className="rounded-full bg-indigo-600 text-white px-5 py-3 font-semibold hover:bg-indigo-700" type="submit">Submit</button>
                {submitted && <span className="text-sm text-green-600">Submitted! Our team will reach out soon.</span>}
              </div>
            </form>
          </Card>
          <div className="space-y-4">
            <Card className="p-6">
              <div className="font-semibold text-slate-900">How membership works</div>
              <ul className="mt-2 text-sm text-slate-600 list-disc pl-5 space-y-1">
                <li>Free to join. Upgrade to workshops via Academy when needed.</li>
                <li>Access playbooks, tools, and partner programs.</li>
                <li>Attend city meetups and online clinics.</li>
              </ul>
            </Card>
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Partner with us</div>
              <div className="mt-2 text-sm text-slate-600">Banks, logistics, SaaS, and corporates can run clinics, offer credits, and sponsor cohorts.</div>
              <a href="#" className="mt-3 inline-flex items-center justify-center rounded-full px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm font-semibold">View Partner Guide</a>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

const Footer: React.FC = () => (
  <footer className="bg-slate-900 text-slate-300">
    <Section className="py-10">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        <div>
          <div className="flex items-center gap-2 font-extrabold text-white text-lg">
            <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-white text-slate-900 font-black">C</span>
            Communities
          </div>
          <p className="mt-3 text-sm text-slate-400 max-w-sm">Grass‑roots DPI adoption for Startups, MSMEs, and Enterprises.</p>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-white">Quick Links</div>
          <ul className="mt-2 space-y-1">
            <li><a href="#directory" className="hover:text-white">Communities</a></li>
            <li><a href="#chapters" className="hover:text-white">Chapters</a></li>
            <li><a href="#events" className="hover:text-white">Events</a></li>
            <li><a href="#join" className="hover:text-white">Join</a></li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="font-semibold text-white">Legal</div>
          <ul className="mt-2 space-y-1">
            <li><a href="#" className="hover:text-white">Terms</a></li>
            <li><a href="#" className="hover:text-white">Privacy</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-8 border-t border-white/10 pt-6 text-xs text-slate-500">© {new Date().getFullYear()} Communities Network. All rights reserved.</div>
    </Section>
  </footer>
);

// ============== OPTIONAL: HashRouter‑lite ==============
// If you want this page to auto‑mount when location.hash === "#/communities",
// you can use the hook below from your main App and conditionally render.
export function useHashRoute(targetHash = "#/communities") {
  const [match, setMatch] = useState<boolean>(window.location.hash === targetHash);
  useEffect(() => {
    const fn = () => setMatch(window.location.hash === targetHash);
    window.addEventListener("hashchange", fn);
    return () => window.removeEventListener("hashchange", fn);
  }, [targetHash]);
  return match;
}

// ============== DEFAULT EXPORT (Standalone Page) ==============
export default function CommunitiesPage(): JSX.Element {
  return (
    <div className="min-h-screen selection:bg-indigo-200 selection:text-slate-900">
      {/* If integrating inside your existing site, place your Nav here and add a link to "#/communities" */}
      <Nav/>
      <Hero/>
      <Directory/>
      <Chapters/>
      <CalendarList/>
      <Benefits/>
      <FAQ/>
      <JoinForm/>
      <Footer/>
    </div>
  );
}
